import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll
} from 'firebase/storage';
import { storage } from '../config/firebase';

class StorageService {
  // Upload file with progress tracking
  uploadFile(file, path, onProgress = null) {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (onProgress) {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress(progress);
          }
        },
        (error) => {
          console.error('Upload failed:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({
              url: downloadURL,
              path: path,
              size: uploadTask.snapshot.totalBytes
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  // Upload image for pass-on
  async uploadPassOnImage(file, userId, passOnId) {
    try {
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${timestamp}.${fileExtension}`;
      const path = `passOns/${userId}/${passOnId}/${fileName}`;
      
      return await this.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading pass-on image:', error);
      throw error;
    }
  }

  // Upload user profile image
  async uploadProfileImage(file, userId) {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `profile.${fileExtension}`;
      const path = `users/${userId}/${fileName}`;
      
      return await this.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw error;
    }
  }

  // Delete file
  async deleteFile(path) {
    try {
      const fileRef = ref(storage, path);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  // Get download URL
  async getDownloadURL(path) {
    try {
      const fileRef = ref(storage, path);
      return await getDownloadURL(fileRef);
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }

  // List files in a directory
  async listFiles(path) {
    try {
      const listRef = ref(storage, path);
      const result = await listAll(listRef);
      
      const files = await Promise.all(
        result.items.map(async (item) => ({
          name: item.name,
          path: item.fullPath,
          url: await getDownloadURL(item)
        }))
      );
      
      return files;
    } catch (error) {
      console.error('Error listing files:', error);
      throw error;
    }
  }

  // Validate file type and size
  validateFile(file, allowedTypes = ['image/jpeg', 'image/png', 'image/gif'], maxSize = 5 * 1024 * 1024) {
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`File type ${file.type} is not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }
    
    if (file.size > maxSize) {
      throw new Error(`File size ${file.size} exceeds maximum allowed size of ${maxSize} bytes`);
    }
    
    return true;
  }

  // Generate unique file name
  generateFileName(originalName) {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    return `${timestamp}_${randomString}.${extension}`;
  }
}

const storageService = new StorageService();
export default storageService;
